/**
 * RouteError — friendly error fallback for any unhandled render error
 * inside a TanStack Router route component.
 *
 * Wired as `defaultErrorComponent` on the router in `main.tsx`, so it
 * covers all 140 authenticated routes + the auth flow + marketing
 * without per-route opt-in. Per-route overrides can pass `errorComponent`
 * for module-specific copy.
 *
 * Visual style mirrors the existing `__root.tsx` `NotFound` so
 * "page not found" and "page errored" share a vocabulary.
 *
 * Caller contract from TanStack Router:
 *   - `error: Error` — the thrown error
 *   - `reset: () => void` — re-mounts the route component for a retry
 *   - `info?: { componentStack?: string }` — React stack (dev only)
 */

import { useEffect } from "react";
import { Link } from "@tanstack/react-router";
import { AlertTriangle, ArrowLeft, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { captureException } from "@/lib/sentry";

interface RouteErrorProps {
  error: Error;
  reset?: () => void;
  info?: { componentStack?: string };
}

export function RouteError({ error, reset }: Readonly<RouteErrorProps>) {
  // Route render errors are swallowed by this boundary (never reach
  // window.onerror), so report them here. Keyed on the error instance —
  // re-renders of the same failure don't re-report.
  useEffect(() => {
    captureException(error);
  }, [error]);

  // Production builds show only the message; dev builds also expose the
  // first line of the stack so the developer doesn't have to open
  // devtools. Stack is never logged or shown in prod.
  const isDev = import.meta.env.DEV;

  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardContent className="flex flex-col items-center gap-4 py-12 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-destructive/10 text-destructive">
            <AlertTriangle aria-hidden className="h-6 w-6" />
          </span>
          <div className="space-y-1">
            <h2 className="text-base font-medium text-gray-900">
              Something went wrong loading this page
            </h2>
            <p className="max-w-md text-sm text-muted-foreground">
              {error.message || "An unexpected error occurred."}
            </p>
            {isDev && error.stack && (
              <pre className="mt-3 max-h-32 overflow-auto rounded-md bg-muted px-3 py-2 text-left text-xs text-muted-foreground">
                {error.stack.split("\n").slice(0, 4).join("\n")}
              </pre>
            )}
          </div>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {reset && (
              <Button onClick={() => reset()} variant="default" className="gap-1.5">
                <RotateCcw aria-hidden className="h-4 w-4" />
                Try Again
              </Button>
            )}
            <Button asChild variant="outline" className="gap-1.5">
              <Link to="/">
                <ArrowLeft aria-hidden className="h-4 w-4" />
                Back to Dashboard
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
